const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage2 = window[this.type];
      const testKey = "__storage_test__";
      storage2.setItem(testKey, "test");
      storage2.removeItem(testKey);
      this.storage = storage2;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage2, prefix) {
    this.storage = storage2;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
const session = new StorageHelper("sessionStorage");
const CONSENT_STORAGE_KEY$2 = "cookieSelections";
const isConsentGranted = () => {
  const selections = local.get(CONSENT_STORAGE_KEY$2);
  return selections?.analytics === true;
};
const idleInject = (inject) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(inject);
  } else {
    setTimeout(inject, 0);
  }
};
const buildGtmSrc = ({ containerId, serverUrl, scriptName }) => {
  if (serverUrl && scriptName) {
    const encodedId = btoa(`id=${containerId}`).replace(/=+$/, "");
    return `${serverUrl}/${scriptName}?P=${encodedId}&page=1`;
  }
  return `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
};
class ThemeScripts {
  /** Loads a script according to its strategy. TypeScript narrows to `GtmScriptConfig` when `id: 'gtm'`. */
  load = (config) => {
    if (config.id === "gtm") {
      this.loadGtm(config);
      return;
    }
    const { id, strategy, src, inline, async: isAsync = true, defer = false, attributes = {}, onLoad, onError } = config;
    this.applyStrategy({
      strategy,
      inject: () => this.inject({ id, src, inline, isAsync, defer, attributes, onLoad, onError })
    });
  };
  /** Handles GTM-specific setup: dataLayer push, URL building, noscript fallback. */
  loadGtm = (config) => {
    const { containerId } = config.gtmConfig;
    this.applyStrategy({
      strategy: config.strategy,
      inject: () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ "gtm.start": (/* @__PURE__ */ new Date()).getTime(), event: "gtm.js" });
        this.inject({
          id: "gtm",
          src: buildGtmSrc(config.gtmConfig),
          isAsync: true,
          defer: false,
          attributes: { "data-gtm": containerId },
          onLoad: config.onLoad,
          onError: config.onError
        });
        if (!document.querySelector(`noscript[data-gtm="${containerId}"]`)) {
          const noscript = document.createElement("noscript");
          noscript.dataset.gtm = containerId;
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.cssText = "display:none;visibility:hidden";
          noscript.appendChild(iframe);
          document.body.insertAdjacentElement("afterbegin", noscript);
        }
      }
    });
  };
  applyStrategy = ({ strategy, inject }) => {
    switch (strategy) {
      case "immediate": {
        inject();
        break;
      }
      case "on-default-consent-set": {
        window.addEventListener("edition:consent-default-set", () => idleInject(inject), { once: true });
        break;
      }
      case "on-consent-granted": {
        window.addEventListener(
          "edition:consent-default-set",
          () => {
            if (isConsentGranted()) {
              idleInject(inject);
              return;
            }
            const waitForConsent = () => {
              if (!isConsentGranted()) return;
              idleInject(inject);
              window.removeEventListener("edition:consent-updated", waitForConsent);
            };
            window.addEventListener("edition:consent-updated", waitForConsent);
          },
          { once: true }
        );
        break;
      }
      case "idle": {
        idleInject(inject);
        break;
      }
      default: {
        const _exhaustive = strategy;
        console.warn("[ThemeScripts] Unknown strategy:", _exhaustive);
      }
    }
  };
  inject = ({ id, src, inline, isAsync, defer, attributes, onLoad, onError }) => {
    if (document.querySelector(`script[data-script-id="${id}"]`)) {
      return;
    }
    if (inline) {
      inline();
    }
    if (!src) {
      return;
    }
    const script = document.createElement("script");
    script.async = isAsync;
    script.defer = defer;
    script.src = src;
    script.dataset.scriptId = id;
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    if (onLoad) {
      script.addEventListener("load", onLoad);
    }
    if (onError) {
      script.addEventListener("error", onError);
    }
    document.head.appendChild(script);
  };
}
const WAITING_CLASS = "media-fade--waiting";
const initMediaFade = () => {
  const reveal = (event) => {
    if (event.target instanceof HTMLImageElement) {
      event.target.classList.remove(WAITING_CLASS);
    }
  };
  document.addEventListener("load", reveal, true);
  document.addEventListener("error", reveal, true);
  const images = Array.from(document.querySelectorAll("img[data-media-fade]"));
  for (const image of images) {
    if (!image.complete) {
      image.classList.add(WAITING_CLASS);
    }
  }
};
const normalizeShopifyId = (id) => String(id).split("/").pop();
const toAbsoluteUrl = (url) => {
  if (!url) {
    return null;
  }
  if (url.startsWith("//")) {
    return `https:${url}`;
  }
  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }
  return url;
};
const CONSENT_STORAGE_KEY$1 = "cookieSelections";
const CONSENT_DENIED_DEFAULTS = { personalization: false, marketing: false, analytics: false };
const CONSENT_KEYS = ["personalization", "marketing", "analytics"];
class CookieBanner extends HTMLElement {
  consentApiReady = false;
  async connectedCallback() {
    await this.setupConsent();
    this.addEventListeners();
    this.activateTab("consent");
    this.syncCheckboxGroups();
    if (this.dataset.forceShow === "true" || await this.shouldShowBanner()) {
      this.showBanner();
    }
  }
  /** Sets gtag consent defaults, fires `edition:consent-default-set`, and optionally loads GTM. */
  setupConsent = async () => {
    this.ensureGtag();
    const selections = local.get(CONSENT_STORAGE_KEY$1) ?? CONSENT_DENIED_DEFAULTS;
    window.gtag?.("consent", "default", {
      functionality_storage: "granted",
      analytics_storage: selections.analytics ? "granted" : "denied",
      personalization_storage: selections.personalization ? "granted" : "denied",
      ad_personalization: selections.personalization ? "granted" : "denied",
      ad_storage: selections.marketing ? "granted" : "denied",
      ad_user_data: selections.marketing ? "granted" : "denied",
      wait_for_update: 3e3
    });
    window.gtag?.("set", "ads_data_redaction", !selections.marketing);
    window.gtag?.("set", "url_passthrough", true);
    window.dispatchEvent(new CustomEvent("edition:consent-default-set"));
    await this.loadShopifyConsentApi();
  };
  addEventListeners = () => {
    this.querySelector("#tab-consent")?.addEventListener("click", () => this.activateTab("consent"));
    this.querySelector("#tab-details")?.addEventListener("click", () => this.activateTab("details"));
    this.querySelector(".allow-all-btn")?.addEventListener("click", this.handleAllowAll);
    this.querySelector(".allow-selected-btn")?.addEventListener("click", this.handleAllowSelected);
    this.querySelector('[data-action="reject"]')?.addEventListener("click", this.handleRejectAll);
    document.getElementById("manage-cookie-consent")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.showBanner();
    });
  };
  activateTab = (tab) => {
    ["consent", "details"].forEach((id) => {
      this.querySelector(`#tab-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("tw:hidden!", id !== tab);
    });
  };
  /**
   * Returns true if the banner should be shown.
   * Prefers localStorage over the Shopify API — if the user has a stored selection we trust it
   * and skip the async API call entirely.
   */
  shouldShowBanner = async () => {
    if (local.get(CONSENT_STORAGE_KEY$1)) {
      return false;
    }
    if (!this.consentApiReady || !window.Shopify?.customerPrivacy?.currentVisitorConsent) {
      return true;
    }
    try {
      const { preferences, analytics, marketing } = await window.Shopify.customerPrivacy.currentVisitorConsent();
      return [preferences, analytics, marketing].every((v) => (v ?? "") === "");
    } catch {
      return true;
    }
  };
  loadShopifyConsentApi = () => {
    if (!window.Shopify?.loadFeatures) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
          if (error) {
            logger.error("Shopify consent-tracking-api failed to load", error);
            resolve();
            return;
          }
          this.consentApiReady = true;
          resolve();
          document.addEventListener("visitorConsentCollected", async () => {
            try {
              const v = await window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
              if (!v) {
                return;
              }
              this.setConsentGroup({ key: "personalization", checked: v.preferences === "yes" });
              this.setConsentGroup({ key: "marketing", checked: v.marketing === "yes" });
              this.setConsentGroup({ key: "analytics", checked: v.analytics === "yes" });
              this.saveSelections();
            } catch (err) {
              logger.error("visitorConsentCollected handler error", err);
            }
          });
        });
      } catch (e) {
        logger.error("Shopify loadFeatures threw", e);
        resolve();
      }
    });
  };
  /** Updates gtag consent, persists to localStorage, and fires `edition:consent-updated`. */
  applyConsent = ({ consent, hide }) => {
    window.gtag?.("consent", "update", {
      functionality_storage: "granted",
      analytics_storage: consent.analytics === "yes" ? "granted" : "denied",
      personalization_storage: consent.preferences === "yes" ? "granted" : "denied",
      ad_personalization: consent.preferences === "yes" ? "granted" : "denied",
      ad_storage: consent.marketing === "yes" ? "granted" : "denied",
      ad_user_data: consent.marketing === "yes" ? "granted" : "denied"
    });
    window.gtag?.("set", "ads_data_redaction", consent.marketing !== "yes");
    this.saveSelections();
    window.dispatchEvent(new CustomEvent("edition:consent-updated"));
    window.Shopify?.customerPrivacy?.setTrackingConsent?.({
      analytics: consent.analytics === "yes",
      marketing: consent.marketing === "yes",
      preferences: consent.preferences === "yes"
    });
    if (hide) {
      this.hideBanner();
    }
  };
  handleAllowAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = true;
      }
    });
    this.applyConsent({ consent: { preferences: "yes", marketing: "yes", analytics: "yes" }, hide: true });
  };
  handleAllowSelected = () => {
    const { personalization, marketing, analytics } = this.getSelections();
    this.applyConsent({
      consent: {
        preferences: personalization ? "yes" : "no",
        marketing: marketing ? "yes" : "no",
        analytics: analytics ? "yes" : "no"
      },
      hide: true
    });
  };
  handleRejectAll = () => {
    this.applyConsent({ consent: { preferences: "no", marketing: "no", analytics: "no" }, hide: true });
  };
  /** Keeps the consent/details tab panels in sync — the same consent key can appear in both tabs. */
  syncCheckboxGroups = () => {
    const checkboxes = Array.from(this.querySelectorAll("input.ed-checkbox[data-consent]"));
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", ({ currentTarget }) => {
        const source = currentTarget;
        checkboxes.forEach((other) => {
          if (other !== source && other.dataset.consent === source.dataset.consent && !other.disabled) {
            other.checked = source.checked;
          }
        });
      });
    });
    const grouped = checkboxes.reduce((acc, cb) => {
      const key = cb.dataset.consent;
      (acc[key] ??= []).push(cb);
      return acc;
    }, {});
    Object.values(grouped).forEach(([source, ...rest]) => {
      rest.forEach((cb) => {
        if (!cb.disabled) {
          cb.checked = source.checked;
        }
      });
    });
  };
  setConsentGroup = ({ key, checked }) => {
    this.querySelectorAll(`input.ed-checkbox[data-consent="${key}"]`).forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = checked;
      }
    });
  };
  saveSelections = () => {
    local.set(CONSENT_STORAGE_KEY$1, this.getSelections());
  };
  getSelections = () => CONSENT_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: !!this.querySelector(`input.ed-checkbox[data-consent="${key}"]`)?.checked
    }),
    { personalization: false, marketing: false, analytics: false }
  );
  get overlay() {
    return this.querySelector("[data-cookie-overlay]") ?? this;
  }
  /**
   * Opens the banner. Applies any previously saved selections to the checkboxes first —
   * if nothing is stored, the HTML defaults remain as-is.
   * Can be triggered externally via `#manage-cookie-consent`.
   */
  showBanner = () => {
    const saved = local.get(CONSENT_STORAGE_KEY$1);
    if (saved) {
      CONSENT_KEYS.forEach((key) => this.setConsentGroup({ key, checked: saved[key] }));
    }
    this.overlay.removeAttribute("hidden");
    this.overlay.classList.remove("tw:hidden!");
  };
  hideBanner = () => {
    this.overlay.classList.add("tw:hidden!");
  };
  /** Creates a `window.gtag` shim that queues calls into `dataLayer` if the real gtag hasn't loaded yet. */
  ensureGtag = () => {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };
}
const TICK_INTERVAL_MS$1 = 3e4;
const DAYS_PER_WEEK = 7;
const MINUTES_PER_HOUR = 60;
const MS_PER_MINUTE = 6e4;
const MS_PER_DAY = 864e5;
const TOKEN_PATTERN = /(__TIME__|__DATE__)/;
const TIME_TOKEN = "__TIME__";
const DATE_TOKEN = "__DATE__";
const UTC_OFFSET_PATTERN = /^([+-])(\d{2})(\d{2})$/;
const SUNDAY = 0;
const SATURDAY = 6;
class DeliveryCutoff extends HTMLElement {
  config = null;
  offsetMinutes = 0;
  timer = null;
  currentVariantId = null;
  controller = null;
  connectedCallback() {
    const config = this.readConfig();
    if (!config) {
      return;
    }
    const offsetMinutes = parseUtcOffset(config.utcOffset);
    if (offsetMinutes === null) {
      logger.warn("[delivery-cutoff] Unreadable UTC offset", config.utcOffset);
      return;
    }
    this.config = config;
    this.offsetMinutes = offsetMinutes;
    this.currentVariantId = config.selectedVariantId ?? null;
    this.controller?.abort();
    this.controller = new AbortController();
    document.addEventListener("variant:change", this.onVariantChange, { signal: this.controller.signal });
    this.render();
    this.timer = window.setInterval(this.render, TICK_INTERVAL_MS$1);
  }
  disconnectedCallback() {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.controller?.abort();
    this.controller = null;
  }
  onVariantChange = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute("id") !== this.config?.formId) {
      return;
    }
    const { variant } = event.detail ?? {};
    this.currentVariantId = variant?.id ?? null;
    this.render();
  };
  readConfig = () => {
    const script = this.querySelector("[data-delivery-cutoff-config]");
    if (!script?.textContent) {
      logger.warn("[delivery-cutoff] No config script found");
      return null;
    }
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      logger.error("[delivery-cutoff] Config is not valid JSON", error);
      return null;
    }
  };
  /** Writes the message for the current moment, or hides the element when no date can be promised. */
  render = () => {
    const target = this.querySelector("[data-delivery-cutoff-message]");
    if (!this.config || !target) {
      return;
    }
    const shopNow = new Date(Date.now() + this.offsetMinutes * MS_PER_MINUTE);
    const nodes = this.preorderMessageFor(shopNow) ?? this.messageFor(shopNow);
    target.replaceChildren(...nodes ?? []);
    this.hidden = nodes === null;
  };
  /**
   * The pre-order variant's message, or null when the ordinary cut-off message should speak: the
   * selected variant is no pre-order, or its stock date has already passed - stock that is in by now
   * ships like anything else, and a promise in the past would read as a mistake.
   */
  preorderMessageFor = (shopNow) => {
    const { preorder, preorderDeliveries, transitDays, locale } = this.config;
    if (!preorder || !preorderDeliveries || this.currentVariantId === null) {
      return null;
    }
    const stockDate = preorderDeliveries[String(this.currentVariantId)];
    if (!stockDate) {
      return null;
    }
    const stockArrival = /* @__PURE__ */ new Date(`${stockDate}T00:00:00Z`);
    if (Number.isNaN(stockArrival.getTime())) {
      logger.warn("[delivery-cutoff] Unreadable pre-order date", stockDate);
      return null;
    }
    const delivery = addBusinessDays(stockArrival, transitDays);
    if (startOfUtcDay(delivery) < startOfUtcDay(shopNow)) {
      return null;
    }
    const withYear = delivery.getUTCFullYear() !== shopNow.getUTCFullYear();
    return buildMessage(preorder, {
      [DATE_TOKEN]: formatDeliveryDate(delivery, locale, withYear)
    });
  };
  messageFor = (shopNow) => {
    const { cutoffs, times, beforeCutoff, afterCutoff, transitDays, locale } = this.config;
    const today = shopNow.getUTCDay();
    const minutesNow = shopNow.getUTCHours() * MINUTES_PER_HOUR + shopNow.getUTCMinutes();
    const todayCutoff = cutoffs[today];
    const madeTodaysCutoff = todayCutoff != null && minutesNow < todayCutoff;
    const daysUntilDispatch = madeTodaysCutoff ? 0 : nextDispatchDayOffset(cutoffs, today);
    if (daysUntilDispatch === null) {
      return null;
    }
    const dispatch = new Date(shopNow.getTime() + daysUntilDispatch * MS_PER_DAY);
    const delivery = addBusinessDays(dispatch, transitDays);
    const template = madeTodaysCutoff ? beforeCutoff[today] : afterCutoff;
    return buildMessage(template, {
      [TIME_TOKEN]: times[today] ?? "",
      [DATE_TOKEN]: formatDeliveryDate(delivery, locale)
    });
  };
}
const buildMessage = (template, values) => template.split(TOKEN_PATTERN).map((part) => {
  if (!(part in values)) {
    return document.createTextNode(part);
  }
  const highlight = document.createElement("span");
  highlight.className = "delivery-cutoff__highlight";
  highlight.textContent = values[part];
  return highlight;
});
const nextDispatchDayOffset = (cutoffs, today) => {
  for (let ahead = 1; ahead <= DAYS_PER_WEEK; ahead += 1) {
    if (cutoffs[(today + ahead) % DAYS_PER_WEEK] != null) {
      return ahead;
    }
  }
  return null;
};
const addBusinessDays = (from, days) => {
  const result = new Date(from.getTime());
  let remaining = days;
  while (remaining > 0) {
    result.setTime(result.getTime() + MS_PER_DAY);
    const weekday = result.getUTCDay();
    if (weekday !== SATURDAY && weekday !== SUNDAY) {
      remaining -= 1;
    }
  }
  return result;
};
const startOfUtcDay = (date) => Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
const formatDeliveryDate = (date, locale, withYear = false) => new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: withYear ? "numeric" : void 0, timeZone: "UTC" }).format(date);
const parseUtcOffset = (offset) => {
  const match = UTC_OFFSET_PATTERN.exec(offset);
  if (!match) {
    return null;
  }
  const [, sign, hours, minutes] = match;
  const total = Number(hours) * MINUTES_PER_HOUR + Number(minutes);
  return sign === "-" ? -total : total;
};
const VISIBLE_CLASS$1 = "is-visible";
const EXPIRED_CLASS = "early-access--expired";
const SIGNED_UP_CLASS = "early-access--signed-up";
const SIGNED_UP_KEY = "signed-up";
const POSTED_VARIANT_KEY = "posted-variant";
const TICK_INTERVAL_MS = 1e3;
const storage$2 = local.namespace("early-access");
const sessionStore = session.namespace("early-access");
class EarlyAccessSignup extends HTMLElement {
  controller = null;
  observer = null;
  tick = null;
  sticky = null;
  modal = null;
  expiredNode = null;
  previouslyFocused = null;
  expiresAt = null;
  releaseAt = null;
  stickyEnabled = false;
  hideAfterRelease = false;
  pastSection = false;
  modalOpen = false;
  expired = false;
  released = false;
  forcedByEditor = false;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.sticky = this.querySelector("[data-early-access-sticky]");
    this.modal = this.querySelector("[data-early-access-modal]");
    this.expiredNode = this.querySelector("[data-early-access-expired]");
    this.expiresAt = this.parseDate(this.dataset.expiresAt);
    this.releaseAt = this.parseDate(this.dataset.releaseAt);
    this.stickyEnabled = this.dataset.stickyEnabled === "true";
    this.hideAfterRelease = this.dataset.hideAfterRelease === "true";
    if (this.isSignedUp && !window.Shopify?.designMode) {
      this.classList.add(SIGNED_UP_CLASS);
    }
    this.handlePostedForm();
    this.bindForms(signal);
    this.bindModal(signal);
    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:select", this.onSectionSelect, { signal });
      document.addEventListener("shopify:section:deselect", this.onSectionDeselect, { signal });
    }
    this.observer = new IntersectionObserver(this.onSectionIntersect, { threshold: 0 });
    this.observer.observe(this);
    this.syncSchedule();
    if (!this.scheduleSettled) {
      this.tick = window.setInterval(this.syncSchedule, TICK_INTERVAL_MS);
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.observer = null;
    if (this.tick !== null) {
      window.clearInterval(this.tick);
      this.tick = null;
    }
    if (this.modalOpen) {
      document.body.style.overflow = "";
      this.modalOpen = false;
    }
    this.sticky?.classList.remove(VISIBLE_CLASS$1);
    this.pastSection = false;
    this.forcedByEditor = false;
  }
  get isSignedUp() {
    return storage$2.get(SIGNED_UP_KEY) === true;
  }
  /** Both "nothing left to wait for" and "no valid dates to wait for" stop the tick. */
  get scheduleSettled() {
    const expirySettled = this.expiresAt === null || this.expired;
    const releaseSettled = this.releaseAt === null || this.released;
    return expirySettled && releaseSettled;
  }
  parseDate(value) {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      logger.warn("[early-access] Ignoring unparseable date", { value });
      return null;
    }
    return parsed;
  }
  /**
   * After a form post Shopify reloads the page with the success (or error) branch rendered in BOTH
   * form copies. The sessionStorage flag written on submit says which copy was actually sent, so
   * the confirmation shows up where the visitor left off - in the popup, or scrolled into view.
   */
  handlePostedForm() {
    const postedVariant = sessionStore.get(POSTED_VARIANT_KEY);
    if (!postedVariant) {
      return;
    }
    sessionStore.remove(POSTED_VARIANT_KEY);
    const success = this.querySelector("[data-early-access-success]");
    const errors = this.querySelector("[data-early-access-errors]");
    if (success) {
      storage$2.set(SIGNED_UP_KEY, true);
      if (!window.Shopify?.designMode) {
        this.classList.add(SIGNED_UP_CLASS);
      }
      if (postedVariant === "modal") {
        this.openModal();
      } else {
        success.scrollIntoView({ block: "center" });
      }
      return;
    }
    if (errors && postedVariant === "modal") {
      this.openModal();
    }
  }
  bindForms(signal) {
    this.querySelectorAll("[data-early-access-form]").forEach((wrapper) => {
      const variant = wrapper.dataset.earlyAccessForm === "modal" ? "modal" : "main";
      const form = wrapper.querySelector("form");
      form?.addEventListener(
        "submit",
        () => {
          sessionStore.set(POSTED_VARIANT_KEY, variant);
        },
        { signal }
      );
    });
  }
  bindModal(signal) {
    this.querySelector("[data-early-access-open]")?.addEventListener("click", this.openModal, { signal });
    this.querySelector("[data-early-access-close]")?.addEventListener("click", this.closeModal, { signal });
    this.querySelector("[data-early-access-backdrop]")?.addEventListener("click", this.closeModal, { signal });
    document.addEventListener("keydown", this.onKeydown, { signal });
  }
  openModal = () => {
    if (!this.modal || this.modalOpen) {
      return;
    }
    this.modalOpen = true;
    this.previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.modal.hidden = false;
    requestAnimationFrame(() => this.modal?.classList.add(VISIBLE_CLASS$1));
    document.body.style.overflow = "hidden";
    this.modal.querySelector('input:not([type="hidden"]), button')?.focus();
    this.syncSticky();
  };
  closeModal = () => {
    if (!this.modal || !this.modalOpen) {
      return;
    }
    this.modalOpen = false;
    this.modal.classList.remove(VISIBLE_CLASS$1);
    this.modal.hidden = true;
    document.body.style.overflow = "";
    this.previouslyFocused?.focus();
    this.previouslyFocused = null;
    this.syncSticky();
  };
  onKeydown = (event) => {
    if (!this.modalOpen) {
      return;
    }
    if (event.key === "Escape") {
      this.closeModal();
      return;
    }
    if (event.key === "Tab" && this.modal) {
      const focusable = Array.from(this.modal.querySelectorAll('a[href], button, input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])'));
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  onSectionIntersect = (entries) => {
    entries.forEach((entry) => {
      this.pastSection = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
    });
    this.syncSticky();
  };
  syncSticky() {
    if (!this.sticky) {
      return;
    }
    const eligible = this.pastSection || this.forcedByEditor;
    const show = this.stickyEnabled && eligible && !this.expired && !this.released && !this.modalOpen;
    if (show) {
      this.sticky.hidden = false;
    }
    this.sticky.classList.toggle(VISIBLE_CLASS$1, show);
  }
  syncSchedule = () => {
    const now = Date.now();
    const expired = this.expiresAt !== null && this.expiresAt.getTime() <= now;
    const released = this.releaseAt !== null && this.releaseAt.getTime() <= now;
    if (expired && !this.expired) {
      this.expired = true;
      this.classList.add(EXPIRED_CLASS);
      if (this.expiredNode) {
        this.expiredNode.hidden = false;
      }
      this.closeModal();
    }
    if (released && !this.released) {
      this.released = true;
      this.closeModal();
      if (this.hideAfterRelease && !window.Shopify?.designMode) {
        this.hidden = true;
      }
    }
    this.syncSticky();
    if (this.scheduleSettled && this.tick !== null) {
      window.clearInterval(this.tick);
      this.tick = null;
    }
  };
  onSectionSelect = (event) => {
    if (this.ownsEvent(event)) {
      this.forcedByEditor = true;
      this.syncSticky();
    }
  };
  onSectionDeselect = (event) => {
    if (this.ownsEvent(event)) {
      this.forcedByEditor = false;
      this.syncSticky();
    }
  };
  ownsEvent(event) {
    return event.target instanceof Node && event.target.contains(this);
  }
}
const DEFAULT_FADE_MS = 800;
const crossFade = (fromCell, toCell, durationMs) => {
  [fromCell, toCell].forEach((cell) => cell.getAnimations().forEach((animation) => animation.cancel()));
  toCell.style.visibility = "visible";
  const options = { duration: durationMs, easing: "ease-in-out", fill: "forwards" };
  const leaving = fromCell.animate({ opacity: [1, 0] }, options);
  const entering = toCell.animate({ opacity: [0, 1] }, options);
  const settled = Promise.all([leaving.finished, entering.finished]).then(() => toCell.style.removeProperty("visibility")).catch(() => toCell.style.removeProperty("visibility"));
  return {
    then: (onFulfilled, onRejected) => settled.then(() => void 0).then(onFulfilled, onRejected),
    complete: () => {
      leaving.finish();
      entering.finish();
    }
  };
};
const defineFadeCarousel = async () => {
  await customElements.whenDefined("effect-carousel");
  const EffectCarousel = customElements.get("effect-carousel");
  if (!EffectCarousel) {
    logger.warn("[fade-carousel] The theme has no effect-carousel to extend");
    return;
  }
  class FadeCarousel extends EffectCarousel {
    createOnChangeAnimationControls(fromCell, toCell) {
      const declared = Number(this.getAttribute("fade-duration"));
      const duration = Number.isFinite(declared) && declared > 0 ? declared : DEFAULT_FADE_MS;
      return crossFade(fromCell, toCell, duration);
    }
  }
  customElements.define("fade-carousel", FadeCarousel);
};
const SHOW_DELAY_MS = 0;
const AUTO_HIDE_MS = 15e3;
const BANNER_GRACE_MS = 3e3;
const DISMISSED_KEY = "dismissed";
const SEEN_KEY = "seen";
const storage$1 = local.namespace("gowish-hint");
const sessionStorageNs = session.namespace("gowish-hint");
class GowishHint extends HTMLElement {
  showTimeoutId;
  hideTimeoutId;
  graceTimeoutId;
  bubble;
  abortController;
  connectedCallback() {
    if (storage$1.get(DISMISSED_KEY) !== null || sessionStorageNs.get(SEEN_KEY) !== null) {
      return;
    }
    this.abortController?.abort();
    this.abortController = new AbortController();
    if (local.get(CONSENT_STORAGE_KEY$1) !== null || document.querySelector("cookie-banner") === null) {
      this.startCountdown();
      return;
    }
    window.addEventListener("edition:consent-updated", this.startCountdown, { once: true, signal: this.abortController.signal });
    this.graceTimeoutId = window.setTimeout(() => {
      if (!this.isBannerOpen()) {
        this.startCountdown();
      }
    }, BANNER_GRACE_MS);
  }
  disconnectedCallback() {
    window.clearTimeout(this.showTimeoutId);
    window.clearTimeout(this.hideTimeoutId);
    window.clearTimeout(this.graceTimeoutId);
    this.abortController?.abort();
    this.bubble?.remove();
    this.bubble = void 0;
  }
  /** True while the cookie banner's overlay is actually rendered on screen. */
  isBannerOpen = () => {
    const banner = document.querySelector("cookie-banner");
    const overlay = banner?.querySelector("[data-cookie-overlay]") ?? banner;
    return overlay !== null && overlay !== void 0 && overlay.getClientRects().length > 0;
  };
  /** Idempotent — both the consent event and the grace fallback may land here. */
  startCountdown = () => {
    if (this.showTimeoutId !== void 0 || this.bubble !== void 0) {
      return;
    }
    this.showTimeoutId = window.setTimeout(this.show, SHOW_DELAY_MS);
  };
  /** The cloud the bubble belongs to: the first fully visible one, else the first in the DOM. */
  findTarget = () => {
    const buttons = Array.from(document.querySelectorAll(".gowish-button--icon"));
    const visible = buttons.filter((button) => {
      const rect = button.getBoundingClientRect();
      return rect.width > 0 && rect.top >= 0 && rect.bottom <= window.innerHeight;
    });
    return visible[0] ?? buttons[0];
  };
  show = () => {
    const target = this.findTarget();
    const anchor = target?.closest("product-card")?.querySelector(".product-card__figure");
    if (!target || !anchor || !this.abortController) {
      return;
    }
    const text = this.dataset.text ?? "";
    if (text === "") {
      logger.warn("GowishHint: missing data-text, not showing");
      return;
    }
    this.bubble = this.buildBubble(text, target.querySelector("svg"));
    anchor.appendChild(this.bubble);
    sessionStorageNs.set(SEEN_KEY, true);
    document.addEventListener("click", this.onDocumentClick, { capture: true, signal: this.abortController.signal });
    this.hideTimeoutId = window.setTimeout(this.hide, AUTO_HIDE_MS);
  };
  onDocumentClick = (event) => {
    const clicked = event.target;
    if (clicked?.closest(".gowish-hint-bubble__close")) {
      event.stopPropagation();
      this.dismiss();
      return;
    }
    if (clicked?.closest(".gowish-button--icon")) {
      this.dismiss();
    }
  };
  buildBubble = (text, cloud) => {
    const bubble = document.createElement("div");
    bubble.className = "gowish-hint-bubble";
    bubble.setAttribute("role", "status");
    if (cloud) {
      bubble.appendChild(cloud.cloneNode(true));
    }
    const message = document.createElement("span");
    message.className = "gowish-hint-bubble__text";
    message.textContent = text;
    const close = document.createElement("button");
    close.type = "button";
    close.className = "gowish-hint-bubble__close";
    close.setAttribute("aria-label", this.dataset.closeLabel ?? "Luk");
    close.textContent = "✕";
    bubble.append(message, close);
    return bubble;
  };
  /** Removes the bubble without persisting anything — an unanswered bubble returns on the next page load. */
  hide = () => {
    window.clearTimeout(this.hideTimeoutId);
    this.abortController?.abort();
    this.bubble?.remove();
    this.bubble = void 0;
  };
  /** The visitor actively closed it (X or the cloud itself) — never show it again. */
  dismiss = () => {
    this.hide();
    storage$1.set(DISMISSED_KEY, true);
  };
}
const SCRIPT_PATTERN = /https:\/\/cdn\.shopify\.com\/extensions\/[^"'\s]+\/assets\/gowish-iframe\.js/;
let scriptLoading = null;
let scriptReady = false;
const isReady = () => scriptReady || scriptLoading === null && document.querySelector('script[src*="/onskeskyen"][src$="/gowish-iframe.js"]') !== null;
const loadScript = (productUrl) => {
  scriptLoading ??= (async () => {
    const html = await (await fetch(productUrl)).text();
    const src = html.match(SCRIPT_PATTERN)?.[0];
    if (!src) {
      throw new Error("gowish-iframe.js not found on the product page");
    }
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        scriptReady = true;
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.append(script);
    });
  })().catch((error) => {
    scriptLoading = null;
    throw error;
  });
  return scriptLoading;
};
class GowishQuickBuy extends HTMLElement {
  controller = null;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener("click", this.onClick, { capture: true, signal: this.controller.signal });
    if (!isReady()) {
      loadScript(this.productUrl).catch((error) => logger.warn("[gowish-quick-buy]", error));
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }
  get productUrl() {
    return new URL(this.getAttribute("product-url") ?? "", window.location.origin).href;
  }
  onClick = (event) => {
    const button = event.target?.closest("button");
    if (!button) {
      return;
    }
    if (!isReady()) {
      event.stopPropagation();
      loadScript(this.productUrl).then(() => button.click()).catch((error) => logger.warn("[gowish-quick-buy]", error));
      return;
    }
    const { href } = window.location;
    const { state } = window.history;
    window.history.replaceState(state, "", this.productUrl);
    window.setTimeout(() => window.history.replaceState(state, "", href));
  };
}
const ROOT_MARGIN = "0px 0px 800px 0px";
const IMAGE_KEEP_MARGIN = "300% 0px";
const BLANK_IMAGE = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
const STATE_TTL_MINUTES = 60;
const storage = session.namespace("infinite-scroll");
let restoreAttempted = false;
const releaseImage = (img) => {
  const src = img.getAttribute("src");
  if (!src || src === BLANK_IMAGE) {
    return;
  }
  img.dataset.releasedSrc = src;
  img.dataset.releasedSrcset = img.getAttribute("srcset") ?? "";
  img.removeAttribute("srcset");
  img.setAttribute("src", BLANK_IMAGE);
};
const restoreImage = (img) => {
  const { releasedSrc, releasedSrcset } = img.dataset;
  if (releasedSrc === void 0) {
    return;
  }
  if (releasedSrcset) {
    img.setAttribute("srcset", releasedSrcset);
  }
  img.setAttribute("src", releasedSrc);
  delete img.dataset.releasedSrc;
  delete img.dataset.releasedSrcset;
};
class InfiniteScroll extends HTMLElement {
  observer = null;
  cardObserver = null;
  controller = null;
  trigger = null;
  pageUrls = [];
  connectedCallback() {
    this.querySelector(".pagination")?.setAttribute("hidden", "");
    if (!this.nextUrl) {
      return;
    }
    window.addEventListener("pagehide", this.saveState);
    this.cardObserver = new IntersectionObserver(this.onCardIntersect, { rootMargin: IMAGE_KEEP_MARGIN });
    this.productList?.querySelectorAll("product-card").forEach((card) => this.cardObserver?.observe(card));
    const state = this.takeState();
    if (state) {
      void this.restore(state);
      return;
    }
    if (this.mode === "infinite" && document.readyState !== "complete") {
      window.addEventListener("load", this.start, { once: true });
    } else {
      this.start();
    }
  }
  disconnectedCallback() {
    window.removeEventListener("load", this.start);
    window.removeEventListener("pagehide", this.saveState);
    this.observer?.disconnect();
    this.cardObserver?.disconnect();
    this.controller?.abort();
    this.trigger?.removeEventListener("click", this.onTriggerClick);
  }
  get mode() {
    return this.getAttribute("mode") === "load_more" ? "load_more" : "infinite";
  }
  get nextUrl() {
    return this.getAttribute("next-url");
  }
  get productList() {
    return this.closest(".collection")?.querySelector("product-list");
  }
  /** Keyed by path and query, so each filter and sort combination keeps its own grid. */
  get stateKey() {
    return `${window.location.pathname}${window.location.search}`;
  }
  /** Returns the saved grid when this is the page-load element of a back/forward or reload visit, and clears it otherwise. */
  takeState = () => {
    const navigation = performance.getEntriesByType("navigation")[0];
    const canRestore = !restoreAttempted && navigation !== void 0 && navigation.type !== "navigate";
    restoreAttempted = true;
    const state = canRestore ? storage.get(this.stateKey) : null;
    storage.remove(this.stateKey);
    return state && state.pageUrls.length > 0 ? state : null;
  };
  saveState = () => {
    if (this.pageUrls.length === 0) {
      return;
    }
    storage.set(this.stateKey, { pageUrls: this.pageUrls, scrollY: window.scrollY }, STATE_TTL_MINUTES);
  };
  /** Wires the observer or the button once the grid is ready to grow. */
  start = () => {
    if (!this.nextUrl) {
      return;
    }
    if (this.mode === "load_more") {
      this.trigger = this.querySelector("[data-infinite-scroll-trigger]");
      this.trigger?.addEventListener("click", this.onTriggerClick);
      return;
    }
    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: ROOT_MARGIN });
    this.observer.observe(this);
  };
  onIntersect = ([entry]) => {
    if (entry && (entry.isIntersecting || entry.boundingClientRect.top < 0)) {
      void this.loadNext();
    }
  };
  onCardIntersect = (entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      target.querySelectorAll("img").forEach(isIntersecting ? restoreImage : releaseImage);
    });
  };
  onTriggerClick = () => {
    void this.loadNext();
  };
  /** Fetches every page the visit had appended in parallel, adds them in order, then scrolls back. */
  restore = async ({ pageUrls, scrollY }) => {
    this.controller = new AbortController();
    this.dispatchLoading(true);
    this.productList?.querySelectorAll('[reveal-on-scroll="true"]').forEach((item) => {
      item.setAttribute("reveal-on-scroll", "false");
    });
    try {
      const docs = await Promise.all(pageUrls.map((url) => this.fetchPage(url)));
      docs.forEach((doc, index) => this.appendPage({ doc, url: pageUrls[index] ?? "", reveal: false }));
      window.scrollTo({ top: scrollY, behavior: "instant" });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      logger.error("Could not restore the loaded pages", error);
    } finally {
      this.controller = null;
      this.dispatchLoading(false);
    }
    this.start();
  };
  loadNext = async () => {
    const nextUrl = this.nextUrl;
    if (this.controller || !nextUrl) {
      return;
    }
    this.controller = new AbortController();
    this.trigger?.setAttribute("aria-busy", "true");
    this.dispatchLoading(true);
    try {
      const doc = await this.fetchPage(nextUrl, "low");
      const items = this.appendPage({ doc, url: nextUrl, reveal: true });
      if (this.mode === "load_more") {
        items.find((item) => item.matches("product-card"))?.querySelector("a[href]")?.focus();
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      logger.error("Could not load the next page", error);
      this.finish();
    } finally {
      this.controller = null;
      this.trigger?.removeAttribute("aria-busy");
      this.dispatchLoading(false);
    }
    if (this.observer) {
      this.observer.unobserve(this);
      this.observer.observe(this);
    }
  };
  fetchPage = async (pageUrl, priority = "auto") => {
    const url = new URL(pageUrl, window.location.origin);
    url.searchParams.set("section_id", this.getAttribute("section-id") ?? "");
    const response = await fetch(url, { signal: this.controller?.signal, priority });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return new DOMParser().parseFromString(await response.text(), "text/html");
  };
  /** Adds a fetched page's cards to the grid and moves `next-url` on to the page after it. */
  appendPage = ({ doc, url, reveal }) => {
    const newList = doc.querySelector("product-list");
    const productList = this.productList;
    if (!newList || !productList) {
      throw new Error("No product-list to append from or to");
    }
    const items = Array.from(newList.children, (child) => document.importNode(child, true));
    productList.append(...items);
    this.reveal({ items, animate: reveal });
    items.filter((item) => item.matches("product-card")).forEach((card) => this.cardObserver?.observe(card));
    this.pageUrls.push(url);
    history.scrollRestoration = "manual";
    const following = doc.querySelector("infinite-scroll")?.getAttribute("next-url");
    if (following) {
      this.setAttribute("next-url", following);
    } else {
      this.finish();
    }
    return items;
  };
  /** Shows the appended cards, which the theme's CSS keeps at opacity 0 until its own one-time reveal runs. */
  reveal = ({ items, animate }) => {
    const motionSafe = animate && window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
    items.forEach((item) => {
      if (item.getAttribute("reveal-on-scroll") !== "true") {
        return;
      }
      item.setAttribute("reveal-on-scroll", "false");
      if (motionSafe) {
        item.animate(
          [
            { opacity: 0, transform: "translateY(20px)" },
            { opacity: 1, transform: "translateY(0)" }
          ],
          { duration: 300, easing: "ease-out" }
        );
      }
    });
  };
  /** Drives the theme's loading bar, the same events its facet updates use. */
  dispatchLoading = (loading) => {
    document.documentElement.dispatchEvent(new CustomEvent(loading ? "theme:loading:start" : "theme:loading:end", { bubbles: true }));
  };
  /** Stops loading once there are no more pages, or after a failed request so it doesn't retry in a loop. */
  finish = () => {
    this.removeAttribute("next-url");
    this.observer?.disconnect();
    this.observer = null;
    this.querySelector("[data-infinite-scroll-trigger]")?.remove();
    this.trigger = null;
  };
}
const MODAL_STYLE_DELAY_MS = 1;
const styleModal = () => {
  const iframe = document.querySelector("#klaviyo-bis-iframe");
  const iframeDocument = iframe?.contentDocument;
  if (!iframeDocument) {
    return;
  }
  const container = iframeDocument.querySelector("#container");
  const content = iframeDocument.querySelector("#container p");
  const modal = iframeDocument.querySelector("#klaviyo-bis-modal");
  const newsletter = iframeDocument.querySelector("#newsletter_subscription p");
  const label = iframeDocument.querySelector("#container label");
  const button = iframeDocument.querySelector(".btn");
  const closeButton = iframeDocument.querySelector(".close");
  const title = iframeDocument.querySelector(".modal-title");
  const emailInput = iframeDocument.querySelector("#email");
  const variantInput = iframeDocument.querySelector("#variants");
  if (modal) {
    modal.style.top = "15%";
    modal.style.position = "relative";
  }
  if (newsletter) {
    newsletter.style.display = "flex";
  }
  if (variantInput) {
    variantInput.style.borderRadius = "0px";
    variantInput.style.appearance = "none";
    variantInput.style.backgroundImage = `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23171717" stroke-width="1.5"><path d="M6 9l6 6 6-6"/></svg>')`;
    variantInput.style.backgroundRepeat = "no-repeat";
    variantInput.style.backgroundPosition = "right 14px center";
    variantInput.style.backgroundSize = "14px";
    variantInput.style.paddingRight = "40px";
  }
  if (emailInput) {
    emailInput.style.borderRadius = "0px";
  }
  if (label) {
    label.style.fontSize = "12px";
  }
  if (content) {
    content.style.fontSize = "12px";
  }
  if (title) {
    title.style.fontSize = "16px";
    title.style.textTransform = "uppercase";
  }
  if (closeButton) {
    closeButton.style.fontSize = "18px";
  }
  if (button) {
    button.style.textTransform = "uppercase";
    button.style.fontSize = "11px";
    button.style.fontWeight = "700";
    button.style.borderRadius = "0px";
  }
  if (container) {
    container.style.borderRadius = "0px";
    container.style.padding = "20px 17px";
  }
};
const scheduleModalStyling = () => {
  window.setTimeout(styleModal, MODAL_STYLE_DELAY_MS);
};
class KlaviyoBis extends HTMLElement {
  controller = null;
  hiddenTrigger = null;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    const variantId = this.getAttribute("variant-id");
    if (variantId) {
      const trigger = document.createElement("button");
      trigger.type = "button";
      trigger.className = "klaviyo-bis-trigger";
      trigger.setAttribute("data-variant-id", variantId);
      trigger.style.display = "none";
      document.body.append(trigger);
      this.hiddenTrigger = trigger;
    }
    this.addEventListener("click", this.onClick, { signal });
    this.addEventListener("keydown", this.onKeydown, { signal });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.hiddenTrigger?.remove();
    this.hiddenTrigger = null;
  }
  onClick = (event) => {
    const target = event.target;
    if (target?.closest("[data-klaviyo-bis-dummy]")) {
      this.openModal();
    }
  };
  /** The dummy is a div with role="button", so Enter and Space have to be wired by hand. */
  onKeydown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    const target = event.target;
    if (!target?.closest("[data-klaviyo-bis-dummy]")) {
      return;
    }
    event.preventDefault();
    this.openModal();
  };
  openModal() {
    if (!this.hiddenTrigger) {
      logger.warn("[klaviyo-bis] Dummy clicked but the element has no variant-id, nothing to sign up for");
      return;
    }
    this.hiddenTrigger.click();
    scheduleModalStyling();
  }
}
const sha256 = async (value) => {
  if (!value) {
    return null;
  }
  try {
    const encoded = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
};
const resolveUrl = (input) => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.pathname;
  }
  return input.url;
};
const activeCurrency = () => window.Shopify?.currency?.active ?? "";
const parseRequestQuantities = (init) => {
  const quantities = /* @__PURE__ */ new Map();
  const body = init?.body;
  if (!body) {
    return quantities;
  }
  try {
    if (typeof body === "string") {
      const parsed = JSON.parse(body);
      if (Array.isArray(parsed.items)) {
        parsed.items.forEach((item) => quantities.set(normalizeShopifyId(item.id), Number(item.quantity) || 1));
      } else if (parsed.id != null) {
        quantities.set(normalizeShopifyId(parsed.id), Number(parsed.quantity) || 1);
      }
    }
    if (body instanceof URLSearchParams || body instanceof FormData) {
      const id = body.get("id");
      if (id != null) {
        quantities.set(normalizeShopifyId(String(id)), Number(body.get("quantity")) || 1);
      }
    }
  } catch {
  }
  return quantities;
};
const buildCartItem = (item, quantity) => ({
  item_name: item.product_title,
  item_id: normalizeShopifyId(item.product_id),
  item_brand: item.vendor,
  item_category: item.product_type,
  item_type: item.product_type,
  item_variant: normalizeShopifyId(item.variant_id),
  item_variant_name: item.variant_title,
  item_image: toAbsoluteUrl(item.featured_image?.url),
  item_url: toAbsoluteUrl(item.url),
  price: item.price / 100,
  sku: item.sku,
  quantity
});
const diffCart = ({ snapshot, newCart, onAddToCart, onRemoveFromCart }) => {
  const currency = activeCurrency();
  const snapshotMap = new Map(snapshot.items.map((item) => [item.key, item]));
  const newMap = new Map(newCart.items.map((item) => [item.key, item]));
  snapshotMap.forEach((oldItem, key) => {
    const newQty = newMap.get(key)?.quantity ?? 0;
    if (newQty < oldItem.quantity) {
      const qtyRemoved = oldItem.quantity - newQty;
      onRemoveFromCart({
        event: "edition_remove_from_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: oldItem.price / 100 * qtyRemoved,
          items: [buildCartItem(oldItem, qtyRemoved)]
        }
      });
    }
  });
  newMap.forEach((newItem, key) => {
    const oldQty = snapshotMap.get(key)?.quantity ?? 0;
    if (newItem.quantity > oldQty) {
      const qtyAdded = newItem.quantity - oldQty;
      onAddToCart({
        event: "edition_add_to_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: newItem.price / 100 * qtyAdded,
          items: [buildCartItem(newItem, qtyAdded)]
        }
      });
    }
  });
};
const initCartInterceptor = ({ onAddToCart, onRemoveFromCart }) => {
  const originalFetch = window.fetch.bind(window);
  const fetchCart = () => originalFetch("/cart.json").then((r) => r.json());
  window.fetch = async (input, init) => {
    const url = resolveUrl(input);
    if (url.includes("/cart/add")) {
      const response = await originalFetch(input, init);
      const requestQuantities = parseRequestQuantities(init);
      response.clone().json().then((data) => {
        const items = "items" in data ? data.items : [data];
        const currency = activeCurrency();
        items.forEach((item) => {
          const addedQuantity = requestQuantities.get(normalizeShopifyId(item.variant_id)) ?? 1;
          onAddToCart({
            event: "edition_add_to_cart",
            ecommerce: {
              timestamp: (/* @__PURE__ */ new Date()).toISOString(),
              url: window.location.href,
              currency,
              value: item.price / 100 * addedQuantity,
              items: [
                {
                  item_name: item.product_title,
                  item_id: normalizeShopifyId(item.product_id),
                  item_brand: item.vendor ?? null,
                  item_category: item.product_type,
                  item_type: item.product_type,
                  item_variant: normalizeShopifyId(item.variant_id),
                  item_variant_name: item.variant_title,
                  item_image: toAbsoluteUrl(item.featured_image?.url),
                  item_url: toAbsoluteUrl(item.url),
                  price: item.price / 100,
                  sku: item.sku ?? null,
                  quantity: addedQuantity
                }
              ]
            }
          });
        });
      }).catch(() => {
      });
      return response;
    }
    if (url.includes("/cart/update") || url.includes("/cart/change")) {
      const snapshot = await fetchCart();
      const response = await originalFetch(input, init);
      const newCart = await fetchCart();
      diffCart({ snapshot, newCart, onAddToCart, onRemoveFromCart });
      return response;
    }
    return originalFetch(input, init);
  };
};
const CONSENT_STORAGE_KEY = "cookieSelections";
const DL_QUEUE = { KEY: "dl_pending_queue", TTL_MINUTES: 30, MAX_SIZE: 20 };
const PAGE_DATA_DEFAULTS = {
  pageType: "other",
  product: null,
  collection: null,
  searchQuery: null,
  customer: null
};
class DataLayer {
  /**
   * Registers the two window event listeners that drive the lifecycle.
   * Must be called once from `app.ts` — safe to call before `CookieBanner` connects.
   *
   * - `edition:consent-default-set` → runs full initialisation (once).
   * - `edition:consent-updated`     → flushes the session queue.
   */
  init = () => {
    window.addEventListener(
      "edition:consent-default-set",
      () => {
        this.initialize().catch((err) => logger.error("DataLayer init failed", err));
      },
      { once: true }
    );
    window.addEventListener("edition:consent-updated", () => {
      this.flushQueue();
    });
  };
  /** Runs the full initialisation sequence — pushes context, user, and page events then wires up interceptors. */
  initialize = async () => {
    window.dataLayer = window.dataLayer || [];
    const pageData = this.readPageData();
    this.pushLocation();
    this.pushContextData({ pageType: pageData.pageType });
    this.pushDeviceData();
    if (pageData.customer) {
      await this.pushUserData({ customer: pageData.customer });
    }
    await this.pushPageTypeEvent({ pageData });
    initCartInterceptor({
      onAddToCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" }),
      onRemoveFromCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" })
    });
    this.bindFormListeners();
    window.editionDataLayer = {
      trackCartView: this.trackCartView,
      trackAddToCart: this.trackAddToCart,
      trackRemoveFromCart: this.trackRemoveFromCart
    };
  };
  /** Parses the `#datalayer-data` JSON script tag injected by `datalayer-data.liquid`. Returns safe defaults on failure. */
  readPageData = () => {
    const el = document.getElementById("datalayer-data");
    if (!el?.textContent) {
      return PAGE_DATA_DEFAULTS;
    }
    try {
      return JSON.parse(el.textContent);
    } catch {
      logger.warn("Failed to parse #datalayer-data JSON");
      return PAGE_DATA_DEFAULTS;
    }
  };
  /**
   * Pushes a payload to `window.dataLayer` if the required consent is granted,
   * otherwise holds it in `sessionStorage` for later flushing.
   */
  pushToDataLayer = ({ payload, consentRequirement }) => {
    payload.event.startsWith("edition_");
    if (this.checkConsent({ requirement: consentRequirement })) {
      window.dataLayer.push(payload);
    } else {
      this.queueEvent({ payload, consentRequirement });
    }
  };
  /** Reads consent state from localStorage — same key written by `CookieBanner`. */
  checkConsent = ({ requirement }) => {
    if (requirement === "none") {
      return true;
    }
    const selections = local.get(CONSENT_STORAGE_KEY);
    if (!selections) {
      return false;
    }
    if (requirement === "analytics") {
      return selections.analytics;
    }
    if (requirement === "marketing") {
      return selections.marketing;
    }
    return false;
  };
  /** Appends an event to the queue, dropping the oldest if the cap is reached. Persists for 30 minutes across page loads. */
  queueEvent = (event) => {
    const queue = this.getQueue();
    if (queue.length >= DL_QUEUE.MAX_SIZE) {
      queue.shift();
    }
    queue.push(event);
    local.set(DL_QUEUE.KEY, queue, DL_QUEUE.TTL_MINUTES);
  };
  /** Returns the current queue from localStorage, or an empty array if nothing is queued or TTL has expired. */
  getQueue = () => local.get(DL_QUEUE.KEY) ?? [];
  /**
   * Replays all queued events that now have consent, leaving the rest in place.
   * Called automatically on `edition:consent-updated`.
   */
  flushQueue = () => {
    const queue = this.getQueue();
    if (!queue.length) {
      return;
    }
    const remaining = [];
    queue.forEach(({ payload, consentRequirement }) => {
      if (this.checkConsent({ requirement: consentRequirement })) {
        this.refreshTimestamp(payload);
        window.dataLayer.push(payload);
      } else {
        remaining.push({ payload, consentRequirement });
      }
    });
    local.set(DL_QUEUE.KEY, remaining, DL_QUEUE.TTL_MINUTES);
    queue.length - remaining.length;
  };
  /** Queued events can be flushed up to 30 minutes after they occurred — restamp so downstream dedup windows see the flush time. */
  refreshTimestamp = (payload) => {
    const now = (/* @__PURE__ */ new Date()).toISOString();
    if ("ecommerce" in payload && "timestamp" in payload.ecommerce) {
      payload.ecommerce.timestamp = now;
    } else if ("timestamp" in payload) {
      payload.timestamp = now;
    }
  };
  /** Pushes the current page URL breakdown and shop domain. No consent required. */
  pushLocation = () => {
    const { hash, host, hostname, href, origin, pathname, port, protocol, search } = window.location;
    this.pushToDataLayer({
      payload: {
        event: "location",
        location: { hash, host, hostname, href, origin, pathname, port, protocol, search },
        shop: window.Shopify?.shop ?? null
      },
      consentRequirement: "none"
    });
  };
  /** Pushes page type, title, and URL. No consent required. */
  pushContextData = ({ pageType }) => {
    this.pushToDataLayer({
      payload: {
        event: "contextData",
        page: { type: pageType, title: document.title, url: window.location.href }
      },
      consentRequirement: "none"
    });
  };
  /** Pushes browser/device signals — language, screen size, user agent, cookie state. No consent required. */
  pushDeviceData = () => {
    this.pushToDataLayer({
      payload: {
        event: "deviceData",
        encoding: document.characterSet,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent
      },
      consentRequirement: "none"
    });
  };
  /** Hashes PII fields with SHA-256 before pushing — email, phone, first/last name. */
  pushUserData = async ({ customer }) => {
    const [sha256Email, sha256Phone, sha256FirstName, sha256LastName] = await Promise.all([
      customer.email ? sha256(customer.email.toLowerCase().trim()) : null,
      customer.phone ? sha256(customer.phone.replace(/\D/g, "")) : null,
      customer.first_name ? sha256(customer.first_name.toLowerCase().trim()) : null,
      customer.last_name ? sha256(customer.last_name.toLowerCase().trim()) : null
    ]);
    const addr = customer.default_address;
    const hasAddress = Boolean(customer.first_name ?? customer.last_name ?? addr);
    this.pushToDataLayer({
      payload: {
        event: "userData",
        customer: {
          id: customer.id != null ? String(customer.id) : null,
          email: customer.email ?? null,
          phone_number: customer.phone ?? null,
          sha256_email_address: sha256Email,
          sha256_phone_number: sha256Phone,
          address: hasAddress ? {
            first_name: customer.first_name ?? null,
            last_name: customer.last_name ?? null,
            street: addr?.address1 ?? null,
            city: addr?.city ?? null,
            region: addr?.province ?? null,
            postal_code: addr?.zip ?? null,
            country: addr?.country_code ?? null,
            sha256_first_name: sha256FirstName,
            sha256_last_name: sha256LastName
          } : null
        }
      },
      consentRequirement: "analytics"
    });
  };
  /**
   * Dispatches the page-type-specific event based on `pageData.pageType`.
   * For `search`, reads `?q` from the URL rather than relying on Liquid output.
   */
  pushPageTypeEvent = async ({ pageData }) => {
    switch (pageData.pageType) {
      case "product": {
        if (pageData.product) {
          this.pushViewItem({ product: pageData.product });
        }
        break;
      }
      case "collection": {
        if (pageData.collection) {
          this.pushCollectionView({ collection: pageData.collection });
        }
        break;
      }
      case "search": {
        const query = new URLSearchParams(window.location.search).get("q") ?? pageData.searchQuery;
        if (query) {
          this.pushSearch({ query });
        }
        break;
      }
      case "cart": {
        await this.trackCartView();
        break;
      }
      case "other": {
        break;
      }
      default: {
        const _exhaustive = pageData.pageType;
        logger.warn("Unhandled DataLayer page type", _exhaustive);
      }
    }
  };
  /** Pushes `edition_view_item` using the URL-selected variant, falling back to the first variant. Requires analytics consent. */
  pushViewItem = ({ product }) => {
    const urlVariantId = new URLSearchParams(window.location.search).get("variant");
    const variant = (urlVariantId ? product.variants.find((v) => normalizeShopifyId(v.id) === urlVariantId) : null) ?? product.variants[0];
    if (!variant) {
      return;
    }
    this.pushToDataLayer({
      payload: {
        event: "edition_view_item",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: variant.price / 100,
          items: [
            {
              item_name: product.title,
              item_id: normalizeShopifyId(product.id),
              item_brand: product.vendor,
              item_category: product.type,
              item_type: product.type,
              item_variant: normalizeShopifyId(variant.id),
              item_variant_name: variant.title,
              item_image: toAbsoluteUrl(variant.featured_image?.src ?? product.featured_image),
              item_url: toAbsoluteUrl(`/products/${product.handle}`),
              price: variant.price / 100,
              sku: variant.sku ?? null,
              quantity: 1
            }
          ]
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_collection_view` with all currently-rendered products (first 30 from Liquid). Requires analytics consent. */
  pushCollectionView = ({ collection }) => {
    const { title, handle, products } = collection;
    this.pushToDataLayer({
      payload: {
        event: "edition_collection_view",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: 0,
          item_list_name: title,
          item_list_id: handle,
          item_list_products_count: products.length,
          // GA4 rejects events with more than 200 items
          items: products.flatMap(
            (p) => p.variants.map(
              (v) => ({
                item_name: p.title,
                item_id: normalizeShopifyId(p.id),
                item_brand: p.vendor,
                item_category: p.type,
                item_type: p.type,
                item_variant: normalizeShopifyId(v.id),
                item_variant_name: v.title,
                item_image: toAbsoluteUrl(v.featured_image?.src ?? p.featured_image),
                item_url: toAbsoluteUrl(`/products/${p.handle}`),
                price: v.price / 100,
                sku: v.sku ?? null
              })
            )
          ).slice(0, 200)
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_search` with the query string only — no result products. Requires analytics consent. */
  pushSearch = ({ query }) => {
    this.pushToDataLayer({
      payload: {
        event: "edition_search",
        search_term: query,
        ecommerce: { timestamp: (/* @__PURE__ */ new Date()).toISOString(), url: window.location.href }
      },
      consentRequirement: "analytics"
    });
  };
  /** Fetches `/cart.json` and pushes `edition_cart_view`. Call this when a cart drawer opens. */
  trackCartView = async () => {
    try {
      const response = await fetch("/cart.json");
      const cart = await response.json();
      this.pushToDataLayer({
        payload: {
          event: "edition_cart_view",
          ecommerce: {
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            url: window.location.href,
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map(
              (item) => ({
                item_name: item.product_title,
                item_id: normalizeShopifyId(item.product_id),
                item_brand: item.vendor,
                item_category: item.product_type,
                item_type: item.product_type,
                item_variant: normalizeShopifyId(item.variant_id),
                item_variant_name: item.variant_title,
                item_image: toAbsoluteUrl(item.featured_image?.url),
                item_url: toAbsoluteUrl(item.url),
                price: item.price / 100,
                sku: item.sku,
                quantity: item.quantity
              })
            )
          }
        },
        consentRequirement: "analytics"
      });
    } catch (err) {
      logger.error("DataLayer: failed to fetch cart", err);
    }
  };
  /** Manually push an `edition_add_to_cart` event — use when a custom flow bypasses `/cart/add.js`. */
  trackAddToCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Manually push an `edition_remove_from_cart` event — use when a custom flow bypasses `/cart/update.js`. */
  trackRemoveFromCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Listens for contact form submits on `document` and distinguishes newsletter vs contact events by form fields. */
  bindFormListeners = () => {
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const action = form.getAttribute("action") ?? "";
      if (!action.includes("/contact")) {
        return;
      }
      const elements = Array.from(form.elements).filter((el) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
      const getField = (name) => elements.find((el) => el.name === name)?.value ?? null;
      const isNewsletter = elements.some((el) => el.name === "contact[tags]" && el instanceof HTMLInputElement && el.value === "newsletter");
      if (isNewsletter) {
        this.pushToDataLayer({
          payload: {
            event: "edition_newsletter_subscribe",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_email: getField("contact[email]")
          },
          consentRequirement: "analytics"
        });
      } else {
        this.pushToDataLayer({
          payload: {
            event: "edition_contact_form_submit",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_name: getField("contact[Name]"),
            customer_email: getField("contact[email]"),
            customer_phone: getField("contact[Phone number]"),
            comment: getField("contact[Comment]")
          },
          consentRequirement: "analytics"
        });
      }
    });
  };
}
const COUNT_PLACEHOLDER = "[count]";
const DESKTOP_MEDIA_QUERY = "(min-width: 700px)";
class LookFeed extends HTMLElement {
  influencerButtons = [];
  filterButtons = [];
  items = [];
  count = null;
  empty = null;
  activeInfluencer = "";
  activeFilter = "";
  connectedCallback() {
    this.influencerButtons = Array.from(this.querySelectorAll("[data-look-feed-influencer]"));
    this.filterButtons = Array.from(this.querySelectorAll("[data-look-feed-filter]"));
    this.items = Array.from(this.querySelectorAll("[data-look-feed-item]"));
    this.count = this.querySelector("[data-look-feed-count]");
    this.empty = this.querySelector("[data-look-feed-empty]");
    this.addEventListener("click", this.handleClick);
    document.addEventListener("click", this.handleDocumentClick);
  }
  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
    document.removeEventListener("click", this.handleDocumentClick);
  }
  handleClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }
    const influencerButton = event.target.closest("[data-look-feed-influencer]");
    if (influencerButton) {
      this.activeInfluencer = influencerButton.dataset.lookFeedInfluencer ?? "";
      this.apply();
      return;
    }
    const filterButton = event.target.closest("[data-look-feed-filter]");
    if (filterButton) {
      this.activeFilter = filterButton.dataset.lookFeedFilter ?? "";
      this.press(this.filterButtons, filterButton);
      this.apply();
    }
  };
  /**
   * Closes every open look the click did not land in. Runs on the document so a click anywhere on
   * the page counts, and only on desktop — on a phone the panels are part of the flow and stay as
   * the reader left them. The look that was just toggled open is its own click's target, so it is
   * passed over.
   */
  handleDocumentClick = (event) => {
    if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    for (const item of this.items) {
      const style = item.querySelector("shop-the-style[open]");
      if (!style || target && style.contains(target)) {
        continue;
      }
      style.removeAttribute("open");
    }
  };
  /** Marks one button of a single-choice row as the pressed one. */
  press = (buttons, active) => {
    for (const button of buttons) {
      button.setAttribute("aria-pressed", String(button === active));
    }
  };
  /**
   * Thins the row of people to those with styles under the active tag, each button carrying its
   * person's tags in a data attribute. When the chosen person falls away with the tag, the choice
   * returns to All rather than leaving the feed filtered by someone no longer on show. The row's
   * pressed states are drawn from the outcome, so they stay right through that fallback.
   */
  syncInfluencers = () => {
    for (const button of this.influencerButtons) {
      const handle = button.dataset.lookFeedInfluencer ?? "";
      if (handle === "") {
        continue;
      }
      const tags = (button.dataset.lookFeedInfluencerFilters ?? "").split(" ");
      const matches = this.activeFilter === "" || tags.includes(this.activeFilter);
      button.hidden = !matches;
      if (!matches && this.activeInfluencer === handle) {
        this.activeInfluencer = "";
      }
    }
    for (const button of this.influencerButtons) {
      const pressed = (button.dataset.lookFeedInfluencer ?? "") === this.activeInfluencer;
      button.setAttribute("aria-pressed", String(pressed));
    }
  };
  apply = () => {
    this.syncInfluencers();
    let visible = 0;
    for (const item of this.items) {
      const matchesInfluencer = this.activeInfluencer === "" || item.dataset.lookInfluencer === this.activeInfluencer;
      const tags = (item.dataset.lookFilters ?? "").split(" ");
      const matchesFilter = this.activeFilter === "" || tags.includes(this.activeFilter);
      const matches = matchesInfluencer && matchesFilter;
      item.hidden = !matches;
      if (matches) {
        visible += 1;
      }
    }
    if (this.count) {
      const template = visible === 1 ? this.count.dataset.countOne : this.count.dataset.countOther;
      if (template) {
        this.count.textContent = template.replace(COUNT_PLACEHOLDER, String(visible));
      }
    }
    if (this.empty) {
      this.empty.hidden = visible > 0;
    }
  };
}
class TermsAndConditions extends HTMLElement {
  abortController = null;
  checkbox = null;
  checkoutButton = null;
  termsPopover = null;
  connectedCallback() {
    this.checkbox = this.querySelector("#cart-drawer-terms-of-service");
    this.checkoutButton = this.querySelector("button[name='checkout']");
    this.termsPopover = this.querySelector("#cart-drawer-terms-popover");
    const acceptButton = this.querySelector("[data-terms-accept]");
    if (!this.checkbox || !this.checkoutButton || !this.termsPopover) {
      return;
    }
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.checkoutButton.addEventListener("click", this.onCheckoutClick, { signal });
    acceptButton?.addEventListener("click", this.onAcceptClick, { signal });
  }
  disconnectedCallback() {
    this.abortController?.abort();
  }
  onCheckoutClick = (event) => {
    if (this.checkbox?.checked) {
      return;
    }
    event.preventDefault();
    void this.termsPopover?.show();
  };
  onAcceptClick = () => {
    if (!this.checkbox || !this.checkoutButton) {
      return;
    }
    this.checkbox.checked = true;
    this.checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    void this.termsPopover?.hide();
    this.checkoutButton.form?.requestSubmit(this.checkoutButton);
  };
}
const VISIBLE_CLASS = "is-visible";
const HEIGHT_VARIABLE = "--scroll-dock-height";
class ScrollDock extends HTMLElement {
  observer = null;
  headerTarget = null;
  headerOnScreen = true;
  footerOnScreen = false;
  sizeObserver = null;
  controller = null;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.sizeObserver = new ResizeObserver(this.onResize);
    this.sizeObserver.observe(this);
    if (window.Shopify?.designMode) {
      document.addEventListener("shopify:section:select", this.onSectionSelect, { signal: this.controller.signal });
      document.addEventListener("shopify:section:deselect", this.onSectionDeselect, { signal: this.controller.signal });
    }
    this.headerTarget = this.findHeader();
    if (!this.headerTarget) {
      logger.warn("[scroll-dock] No header to watch, the dock stays hidden");
      return;
    }
    this.observer = new IntersectionObserver(this.onIntersect, { threshold: 0 });
    this.observer.observe(this.headerTarget);
    const footer = document.querySelector(".shopify-section--footer");
    if (footer) {
      this.observer.observe(footer);
    }
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.observer?.disconnect();
    this.observer = null;
    this.headerTarget = null;
    this.sizeObserver?.disconnect();
    this.sizeObserver = null;
    this.classList.remove(VISIBLE_CLASS);
    document.documentElement.style.removeProperty(HEIGHT_VARIABLE);
  }
  /** The inner `<x-header>`, with the section wrapper as the fallback if the theme ever drops the class. */
  findHeader() {
    return document.querySelector(".shopify-section--header .header") ?? document.querySelector(".shopify-section--header");
  }
  /** One observer watches both ends, so each entry has to say which one it is about. */
  onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (entry.target === this.headerTarget) {
        this.headerOnScreen = entry.isIntersecting;
      } else {
        this.footerOnScreen = entry.isIntersecting;
      }
    });
    this.syncVisibility();
  };
  syncVisibility() {
    this.classList.toggle(VISIBLE_CLASS, !this.headerOnScreen && !this.footerOnScreen);
  }
  /**
   * The theme's own `<height-observer>` cannot do this one: it measures an element in normal flow, and
   * the dock is fixed, so the wrapper would report zero.
   */
  onResize = () => {
    document.documentElement.style.setProperty(HEIGHT_VARIABLE, `${this.offsetHeight}px`);
  };
  /**
   * Nobody can configure a bar they cannot see. The dock is fixed, so the editor's scroll-to-section
   * does nothing for it and the observer would keep it hidden while the merchant is editing it.
   */
  onSectionSelect = (event) => {
    if (this.ownsEvent(event)) {
      this.classList.add(VISIBLE_CLASS);
    }
  };
  onSectionDeselect = (event) => {
    if (this.ownsEvent(event)) {
      this.syncVisibility();
    }
  };
  ownsEvent(event) {
    return event.target instanceof Node && event.target.contains(this);
  }
}
const MOBILE_MEDIA_QUERY = "(max-width: 699px)";
class ShopTheStyle extends HTMLElement {
  static observedAttributes = ["open"];
  toggles = [];
  panel = null;
  connectedCallback() {
    this.toggles = Array.from(this.querySelectorAll("[data-shop-the-style-toggle]"));
    this.panel = this.querySelector("[data-shop-the-style-panel]");
    for (const toggle of this.toggles) {
      toggle.addEventListener("click", this.handleToggle);
    }
    if (this.hasAttribute("open-on-mobile") && window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      this.toggleAttribute("open", true);
    }
    this.syncState();
  }
  disconnectedCallback() {
    for (const toggle of this.toggles) {
      toggle.removeEventListener("click", this.handleToggle);
    }
  }
  attributeChangedCallback() {
    this.syncState();
  }
  handleToggle = () => {
    const open = !this.hasAttribute("open");
    this.toggleAttribute("open", open);
    if (open && this.panel && getComputedStyle(this.panel).position !== "absolute") {
      this.panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };
  /** Carries the `open` attribute out to the buttons and the panel, wherever the change came from. */
  syncState = () => {
    const open = this.hasAttribute("open");
    for (const toggle of this.toggles) {
      toggle.setAttribute("aria-expanded", String(open));
    }
    if (this.panel) {
      this.panel.inert = !open;
    }
  };
}
class LazySection extends HTMLElement {
  observer = null;
  connectedCallback() {
    if (window.Shopify?.designMode) {
      return;
    }
    this.style.display = "block";
    this.style.contentVisibility = "hidden";
    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: "300px" });
    this.observer.observe(this);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
  }
  onIntersect = ([entry]) => {
    if (!entry.isIntersecting) {
      return;
    }
    this.observer?.disconnect();
    this.style.contentVisibility = "";
  };
}
const HIGHLIGHT_DURATION_MS = 1200;
class VariantGate extends HTMLElement {
  controller = null;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { form } = this;
    if (!form) {
      logger.warn("[variant-gate] No form matches form-id", this.getAttribute("form-id"));
      return;
    }
    if (!this.variantInput) {
      logger.warn("[variant-gate] Form has no hidden variant input, leaving the row alone");
      return;
    }
    const { signal } = this.controller;
    this.apply();
    queueMicrotask(this.apply);
    form.addEventListener("variant:change", this.onVariantChange, { signal });
    document.addEventListener("change", this.onOptionChange, { signal });
    document.addEventListener("submit", this.onSubmit, { capture: true, signal });
    this.addEventListener("click", this.onClick, { signal });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }
  /** The product form these buttons submit to. The sticky bar points at the main form, not its own. */
  get form() {
    const formId = this.getAttribute("form-id");
    return formId ? document.forms.namedItem(formId) ?? null : null;
  }
  /**
   * `form.id` resolves to the hidden `<input name="id">`, not to the form's own id attribute —
   * HTMLFormElement lets named controls shadow inherited properties, and the theme relies on it too.
   */
  get variantInput() {
    const field = this.form?.id;
    return field instanceof HTMLInputElement ? field : null;
  }
  /**
   * A variant is chosen only when every option position is checked *and* the theme has written the
   * resolved id into the form. The theme sets that id when its re-render fetch resolves, so without the
   * second test there is a window where the row is open but the form still holds the previous size.
   */
  get isComplete() {
    const form = this.form;
    if (!form) {
      return false;
    }
    const inputs = Array.from(form.elements).filter((element) => element instanceof HTMLInputElement && element.hasAttribute("data-option-position"));
    const positions = new Set(inputs.map((input) => input.dataset.optionPosition));
    const checked = new Set(inputs.filter((input) => input.checked).map((input) => input.dataset.optionPosition));
    if (positions.size === 0) {
      return true;
    }
    const variantInput = this.variantInput;
    return positions.size === checked.size && variantInput !== null && variantInput.value !== "";
  }
  apply = () => {
    const complete = this.isComplete;
    const variantInput = this.variantInput;
    if (variantInput) {
      const fallbackId = this.dataset.fallbackVariantId;
      if (complete && variantInput.value === "" && fallbackId) {
        variantInput.value = fallbackId;
      }
      if (!complete && this.hasNothingChecked) {
        variantInput.value = "";
      }
    }
    this.toggle("[data-atc]", !complete);
    this.toggle("[data-buy-now]", !complete);
    this.toggle("[data-choose-variant]", complete);
    if (complete) {
      this.querySelectorAll("[data-atc]").forEach((button) => button.removeAttribute("aria-busy"));
    }
  };
  /** Only a truly empty selection clears the id — a partial one would wipe what the theme just wrote. */
  get hasNothingChecked() {
    const form = this.form;
    if (!form) {
      return false;
    }
    return !Array.from(form.elements).some((element) => element instanceof HTMLInputElement && element.hasAttribute("data-option-position") && element.checked);
  }
  toggle(selector, hidden) {
    this.querySelectorAll(selector).forEach((element) => {
      element.hidden = hidden;
    });
  }
  onVariantChange = (event) => {
    if (event.target === this.form) {
      this.apply();
    }
  };
  /**
   * Moves the row into a pending state the moment an option is clicked. It deliberately does not open
   * the row: the variant id only lands when the theme's re-render fetch resolves.
   */
  onOptionChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.hasAttribute("data-option-position")) {
      return;
    }
    if (target.getAttribute("form") !== this.getAttribute("form-id")) {
      return;
    }
    if (this.isComplete) {
      this.apply();
      return;
    }
    this.toggle("[data-choose-variant]", true);
    this.querySelectorAll("[data-atc]").forEach((button) => {
      button.hidden = false;
      button.setAttribute("aria-busy", "true");
    });
  };
  /**
   * A hidden submit button is still a submit button, and a form with a single text field submits on
   * Enter with no submitter at all. Capture phase on the document, so this runs before the theme's own
   * submit listener on the form.
   */
  onSubmit = (event) => {
    if (event.target !== this.form || this.isComplete) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.hasNothingChecked) {
      this.revealPicker();
    }
  };
  onClick = (event) => {
    const target = event.target;
    if (target?.closest("[data-choose-variant]")) {
      event.preventDefault();
      this.revealPicker();
      return;
    }
    if (target?.closest("[data-buy-now]")) {
      event.preventDefault();
      void this.buyNow();
    }
  };
  /**
   * Sends the customer to the size picker. Announced first as a cancelable event, so a component that can
   * offer the sizes closer to hand — the sticky bar's inline panel — can take it over. Nothing listening
   * means the scroll still happens, which is what the buy buttons row wants.
   */
  revealPicker() {
    const reveal = new CustomEvent("variant-gate:reveal", { bubbles: true, cancelable: true });
    if (!this.dispatchEvent(reveal)) {
      return;
    }
    const formId = this.getAttribute("form-id");
    const picker = formId ? document.querySelector(`variant-picker[form-id="${formId}"]`) : null;
    if (!picker) {
      return;
    }
    picker.scrollIntoView({ behavior: "smooth", block: "center" });
    picker.classList.remove("is-highlighted");
    void picker.offsetWidth;
    picker.classList.add("is-highlighted");
    window.setTimeout(() => picker.classList.remove("is-highlighted"), HIGHLIGHT_DURATION_MS);
    picker.querySelector("input[data-option-position]:not([disabled])")?.focus({ preventScroll: true });
  }
  /**
   * Adds to cart and goes straight to checkout. Posting the form's own FormData keeps line item
   * properties, quantity and selling plan, and going through fetch keeps the add visible to anything
   * listening on /cart/add.
   */
  async buyNow() {
    const form = this.form;
    const button = this.querySelector("[data-buy-now]");
    if (!form || !button || !form.reportValidity()) {
      return;
    }
    button.setAttribute("aria-busy", "true");
    const root = window.Shopify?.routes?.root ?? "/";
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        body: new FormData(form),
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!response.ok) {
        const body = await response.json();
        throw new Error(body.message ?? response.statusText);
      }
      window.location.assign(`${root}checkout`);
    } catch (error) {
      logger.error("[variant-gate] Buy now failed", error);
      button.removeAttribute("aria-busy");
      document.dispatchEvent(
        new CustomEvent("toast:show", {
          detail: { message: error instanceof Error ? error.message : String(error), tone: "error" }
        })
      );
    }
  }
}
class StickySizePicker extends HTMLElement {
  controller = null;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    if (!this.form) {
      logger.warn("[sticky-size-picker] No form matches form-id", this.getAttribute("form-id"));
      return;
    }
    const { signal } = this.controller;
    this.addEventListener("click", this.onClick, { capture: true, signal });
    this.addEventListener("variant-gate:reveal", this.onReveal, { signal });
    document.addEventListener("keydown", this.onKeydown, { signal });
    document.addEventListener("pointerdown", this.onPointerDown, { signal });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }
  /** The product form the size buttons write into. The bar points at the main form, not one of its own. */
  get form() {
    const formId = this.getAttribute("form-id");
    return formId ? document.forms.namedItem(formId) ?? null : null;
  }
  /**
   * `form.id` resolves to the hidden `<input name="id">`, not to the form's own id attribute —
   * HTMLFormElement lets named controls shadow inherited properties, and the theme relies on it too.
   */
  get variantInput() {
    const field = this.form?.id;
    return field instanceof HTMLInputElement ? field : null;
  }
  get panel() {
    return this.querySelector("[data-sticky-size-panel]");
  }
  get toggle() {
    return this.querySelector("[data-sticky-size-toggle]");
  }
  get isOpen() {
    return this.panel?.hidden === false;
  }
  open = () => {
    const panel = this.panel;
    if (!panel) {
      return;
    }
    panel.hidden = false;
    this.toggle?.setAttribute("aria-expanded", "true");
    panel.querySelector("[data-sticky-size-value]:not([disabled])")?.focus({ preventScroll: true });
  };
  close = (options = {}) => {
    const panel = this.panel;
    if (!panel || panel.hidden) {
      return;
    }
    panel.hidden = true;
    this.toggle?.setAttribute("aria-expanded", "false");
    if (options.restoreFocus) {
      this.toggle?.focus({ preventScroll: true });
    }
  };
  onClick = (event) => {
    const target = event.target;
    if (target?.closest("[data-sticky-size-toggle]")) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
      return;
    }
    if (target?.closest("[data-sticky-size-close]")) {
      event.preventDefault();
      this.close({ restoreFocus: true });
      return;
    }
    const value = target?.closest("[data-sticky-size-value]");
    if (value) {
      event.preventDefault();
      this.selectSize(value);
    }
  };
  onKeydown = (event) => {
    if (event.key === "Escape" && this.isOpen) {
      this.close({ restoreFocus: true });
    }
  };
  onPointerDown = (event) => {
    if (this.isOpen && !event.composedPath().includes(this)) {
      this.close();
    }
  };
  /** Takes over the gate's "send them up to the picker" fallback while the panel is on the page. */
  onReveal = (event) => {
    event.preventDefault();
    this.open();
  };
  /**
   * Writes the chosen size into the main form and submits it.
   *
   * The matching radio is checked without firing `change`, because the theme's `<variant-picker>`
   * delegates `change` on `document.body` and would start a section render before the add goes out.
   * The event is dispatched afterwards instead, so the page catches up — picker, price and `?variant=` —
   * while the item is already on its way into the cart.
   */
  selectSize(button) {
    const form = this.form;
    const variantInput = this.variantInput;
    const { variantId, optionValueId } = button.dataset;
    if (!form || !variantInput || !variantId) {
      logger.warn("[sticky-size-picker] Cannot add without a form and a variant id");
      return;
    }
    const submitter = this.querySelector("[data-atc]");
    if (!submitter || submitter.disabled || submitter.getAttribute("aria-busy") === "true") {
      return;
    }
    const radio = optionValueId ? this.findOptionInput(form, optionValueId) : null;
    if (radio) {
      radio.checked = true;
    }
    variantInput.value = variantId;
    const preorderInput = form.querySelector("[data-preorder-input]");
    const preorderProperty = button.dataset.preorderProperty;
    if (preorderInput) {
      preorderInput.value = preorderProperty ?? "";
      preorderInput.disabled = !preorderProperty;
    } else if (preorderProperty) {
      logger.warn("[sticky-size-picker] No pre-order input to carry the Preorder property");
    }
    submitter.hidden = false;
    form.requestSubmit(submitter);
    this.close();
    radio?.dispatchEvent(new Event("change", { bubbles: true }));
  }
  findOptionInput(form, optionValueId) {
    return Array.from(form.elements).find((element) => element instanceof HTMLInputElement && element.hasAttribute("data-option-position") && element.value === optionValueId) ?? null;
  }
}
class SizeGuideFit extends HTMLElement {
  controller = null;
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener("click", this.onClick, { signal: this.controller.signal });
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
  }
  onClick = (event) => {
    const option = event.target.closest("[data-fit-option]");
    if (!option) {
      return;
    }
    const label = option.textContent?.trim() ?? "";
    this.style.setProperty("--compare-position", `${option.dataset.fitPosition}%`);
    this.querySelectorAll("[data-fit-summary], [data-fit-label]").forEach((element) => {
      element.textContent = label;
    });
    this.querySelectorAll("[data-fit-option]").forEach((element) => {
      element.setAttribute("aria-pressed", String(element === option));
    });
    const comparison = this.querySelector("[data-fit-comparison]");
    const details = this.querySelector("[data-fit-compare]");
    if (comparison) {
      comparison.hidden = false;
    }
    if (details) {
      details.open = false;
    }
  };
}
const scripts = new ThemeScripts();
const dataLayer = new DataLayer();
scripts.load({
  id: "gtm",
  strategy: "on-default-consent-set",
  gtmConfig: { containerId: "GTM-5CG27XDP" }
});
dataLayer.init();
initMediaFade();
customElements.define("lazy-section", LazySection);
customElements.define("delivery-cutoff", DeliveryCutoff);
customElements.define("variant-gate", VariantGate);
customElements.define("sticky-size-picker", StickySizePicker);
customElements.define("size-guide-fit", SizeGuideFit);
customElements.define("scroll-dock", ScrollDock);
customElements.define("early-access-signup", EarlyAccessSignup);
customElements.define("shop-the-style", ShopTheStyle);
customElements.define("look-feed", LookFeed);
customElements.define("gowish-quick-buy", GowishQuickBuy);
customElements.define("gowish-hint", GowishHint);
customElements.define("klaviyo-bis", KlaviyoBis);
customElements.define("infinite-scroll", InfiniteScroll);
customElements.define("cookie-banner", CookieBanner);
customElements.define("terms-and-conditions", TermsAndConditions);
void defineFadeCarousel();
